/**
 * Timezone arithmetic, built on `Intl` alone — no dependencies, works
 * identically in workerd, Hermes and Node.
 *
 * The central idea: a dose time like "08:00" is a *wall-clock promise*. When a
 * patient flies Hyderabad -> London, 08:00 must stay 08:00 local. So schedules
 * are authored in wall-clock terms and converted to UTC instants here.
 */

import type { LocalDate, LocalTime, Weekday } from './types.js';

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
export const DAY_MS = 86_400_000;

export interface WallClock {
  year: number;
  /** 1-12. */
  month: number;
  /** 1-31. */
  day: number;
  hour: number;
  minute: number;
}

const partsCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let fmt = partsCache.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    partsCache.set(timeZone, fmt);
  }
  return fmt;
}

/**
 * An IANA zone identifier naming a PLACE, e.g. `Asia/Kolkata`.
 *
 * Deliberately stricter than "something `Intl` accepts", because `Intl`
 * accepts a good deal more than it should for this purpose:
 *
 *   EST              Jan 12:00   Jul 12:00
 *   America/New_York Jan 12:00   Jul 13:00
 *
 * `EST` is a fixed offset that never observes daylight saving, so a patient
 * stored under it is reminded an hour early for roughly eight months of the
 * year, silently. Worse, the abbreviations are not consistently wrong: ICU
 * maps `PST` through to `America/Los_Angeles` and gets DST right, while `EST`
 * stays fixed — so there is no way to reason about which is safe. And `IST`
 * means India, Ireland and Israel, two of which change their clocks.
 *
 * This is the whole promise of the scheduler. `fixed_times` means 08:00 stays
 * 08:00 across a DST change, and it can only hold if the stored zone is a
 * place whose rules a tz database can follow.
 *
 * `Etc/GMT+5` and the bare `UTC` are rejected for the same reason: they are
 * offsets, not places, and nobody's breakfast is at UTC.
 */
const IANA_ZONE = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+){1,2}$/;

export function isValidTimeZone(timeZone: string): boolean {
  if (!IANA_ZONE.test(timeZone)) return false;
  // `Etc/*` has the right shape but names an offset rather than a place, and
  // its signs are inverted (`Etc/GMT+5` is UTC-5) — a footgun with no upside
  // for someone recording where a patient lives.
  if (timeZone.startsWith('Etc/')) return false;
  try {
    formatterFor(timeZone).format(0);
    return true;
  } catch {
    return false;
  }
}

/** Decompose a UTC instant into the wall clock a patient in `timeZone` would read. */
export function toWallClock(instant: number, timeZone: string): WallClock {
  const parts = formatterFor(timeZone).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const p = parts.find((x) => x.type === type);
    if (!p) throw new Error(`Intl did not return a "${type}" part for ${timeZone}`);
    return Number(p.value);
  };
  // Some engines render midnight as hour 24 under hour12:false.
  const hour = get('hour') % 24;
  return { year: get('year'), month: get('month'), day: get('day'), hour, minute: get('minute') };
}

/**
 * Offset of `timeZone` from UTC at a given instant, in ms.
 * Positive east of Greenwich (IST = +19_800_000).
 */
export function offsetAt(instant: number, timeZone: string): number {
  const parts = formatterFor(timeZone).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const p = parts.find((x) => x.type === type);
    if (!p) throw new Error(`Intl did not return a "${type}" part for ${timeZone}`);
    return Number(p.value);
  };
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  );
  // Intl truncates to whole seconds; floor the instant before diffing so the
  // result is an exact offset rather than offset-minus-milliseconds.
  return asUtc - Math.floor(instant / 1000) * 1000;
}

/**
 * Convert a wall clock in `timeZone` to a UTC instant.
 *
 * DST edge cases, decided explicitly because "whatever the library does" is not
 * an acceptable answer when the output is a medication alarm:
 *
 * - **Spring-forward gap** (the wall time never happens, e.g. 02:30 when clocks
 *   jump 02:00 -> 03:00): returns the first instant *after* the gap. The dose is
 *   taken slightly late rather than skipped entirely.
 * - **Fall-back overlap** (the wall time happens twice): returns the *first*
 *   (pre-transition) occurrence, so the patient is reminded earlier rather than
 *   later. Never produces two doses for one slot.
 */
export function wallClockToInstant(wc: WallClock, timeZone: string): number {
  const naive = Date.UTC(wc.year, wc.month - 1, wc.day, wc.hour, wc.minute, 0, 0);

  // First guess: assume the offset that applies at the naive instant.
  const guessOffset = offsetAt(naive, timeZone);
  let instant = naive - guessOffset;

  // Refine: the offset at our candidate may differ (we crossed a transition).
  const refinedOffset = offsetAt(instant, timeZone);
  if (refinedOffset !== guessOffset) {
    instant = naive - refinedOffset;
  }

  // Verify by round-tripping. A mismatch means the requested wall time does not
  // exist -- we are inside a spring-forward gap.
  const check = toWallClock(instant, timeZone);
  if (check.hour !== wc.hour || check.minute !== wc.minute) {
    const before = offsetAt(instant - 2 * HOUR_MS, timeZone);
    const after = offsetAt(instant + 2 * HOUR_MS, timeZone);
    if (after > before) {
      // Shift the requested wall time forward by the size of the gap, which is
      // the standard disambiguation (Temporal's `compatible` mode): 02:30 on a
      // spring-forward day becomes 03:30 rather than being skipped.
      //   naive + gap - after  ===  naive - before
      return naive - before;
    }
  }
  return instant;
}

/** `YYYY-MM-DD` + `HH:MM` in `timeZone` -> UTC instant. */
export function localDateTimeToInstant(
  date: LocalDate,
  time: LocalTime,
  timeZone: string,
): number {
  const d = parseLocalDate(date);
  const t = parseLocalTime(time);
  return wallClockToInstant({ ...d, hour: t.hour, minute: t.minute }, timeZone);
}

export function parseLocalDate(date: LocalDate): { year: number; month: number; day: number } {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) throw new Error(`Invalid LocalDate "${date}", expected YYYY-MM-DD`);
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12) throw new Error(`Invalid month in "${date}"`);
  if (day < 1 || day > 31) throw new Error(`Invalid day in "${date}"`);
  return { year, month, day };
}

export function parseLocalTime(time: LocalTime): { hour: number; minute: number } {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!m) throw new Error(`Invalid LocalTime "${time}", expected HH:MM`);
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) throw new Error(`Out-of-range LocalTime "${time}"`);
  return { hour, minute };
}

export function formatLocalDate(wc: Pick<WallClock, 'year' | 'month' | 'day'>): LocalDate {
  return `${pad(wc.year, 4)}-${pad(wc.month, 2)}-${pad(wc.day, 2)}`;
}

export function formatLocalTime(wc: Pick<WallClock, 'hour' | 'minute'>): LocalTime {
  return `${pad(wc.hour, 2)}:${pad(wc.minute, 2)}`;
}

function pad(n: number, width: number): string {
  return String(n).padStart(width, '0');
}

/** Civil-date arithmetic, independent of any timezone. */
export function addDaysToLocalDate(date: LocalDate, days: number): LocalDate {
  const { year, month, day } = parseLocalDate(date);
  const utc = Date.UTC(year, month - 1, day) + days * DAY_MS;
  const d = new Date(utc);
  return formatLocalDate({
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
  });
}

/** Whole days between two civil dates (`b - a`). */
export function daysBetweenLocalDates(a: LocalDate, b: LocalDate): number {
  const pa = parseLocalDate(a);
  const pb = parseLocalDate(b);
  return Math.round(
    (Date.UTC(pb.year, pb.month - 1, pb.day) - Date.UTC(pa.year, pa.month - 1, pa.day)) / DAY_MS,
  );
}

/** ISO weekday (1 = Monday ... 7 = Sunday) for a civil date. */
export function weekdayOfLocalDate(date: LocalDate): Weekday {
  const { year, month, day } = parseLocalDate(date);
  const dow = new Date(Date.UTC(year, month - 1, day)).getUTCDay(); // 0 = Sunday
  return (dow === 0 ? 7 : dow) as Weekday;
}

/** The civil date a patient in `timeZone` is living on at `instant`. */
export function localDateAt(instant: number, timeZone: string): LocalDate {
  return formatLocalDate(toWallClock(instant, timeZone));
}

export function compareLocalDates(a: LocalDate, b: LocalDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export { MINUTE_MS, HOUR_MS };

/**
 * "GMT+05:30" for a zone at an instant — RX-REM-16.
 *
 * Said beside a time whenever the reader may be somewhere else: a caregiver
 * in Dubai reading about a dose in Hyderabad, a traveller whose reminders
 * stayed on home time. Computed from the wall clock rather than asked of
 * Intl's time-zone names, which differ between engines and are missing on
 * some phones. Accounts for daylight saving at that instant.
 */
export function zoneOffsetLabel(timeZone: string, at: number): string {
  const wc = toWallClock(at, timeZone);
  const asUtc = Date.UTC(wc.year, wc.month - 1, wc.day, wc.hour, wc.minute);
  const minutes = Math.round((asUtc - Math.floor(at / 60_000) * 60_000) / 60_000);
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  return `GMT${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}


const sameClockMemo = new Map<string, boolean>();

/**
 * Whether two zones keep the same clock — not whether they have the same name.
 *
 * The tz database has many names for one clock: a phone reporting the old
 * `Asia/Calcutta` is on exactly the time of a record kept in `Asia/Kolkata`
 * (Kiev and Kyiv, Saigon and Ho_Chi_Minh, Buenos_Aires and its Argentina/
 * twin…). Comparing names told a person in Hyderabad their phone was "on a
 * different time" — both GMT+05:30 — and offered to "switch" them to it
 * (owner's report, 2 Oct 2026). Equal offsets on every day of the coming
 * year mean the same clock, daylight saving included; a zone that differs
 * only in some far-off rule change is not worth asking a person about.
 */
export function sameClock(a: string, b: string, from = Date.now()): boolean {
  if (a === b) return true;
  const key = a < b ? `${a}|${b}` : `${b}|${a}`;
  const known = sameClockMemo.get(key);
  if (known !== undefined) return known;
  let same = true;
  try {
    const start = Math.floor(from / 86_400_000) * 86_400_000;
    for (let day = 0; day < 400 && same; day++) {
      const at = start + day * 86_400_000 + 12 * 3_600_000;
      if (zoneOffsetLabel(a, at) !== zoneOffsetLabel(b, at)) same = false;
    }
  } catch {
    same = false;
  }
  sameClockMemo.set(key, same);
  return same;
}
